import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Spinner, Suspense } from 'core/components';
import { deepClone, safelyParseJSON } from 'core/util';
import { timezone } from 'core/util/dateUtils';

export default function withHybridTopoSettings(WrappedComponent) {
  @inject('$hybridMap', '$moduleConfig', '$auth')
  @observer
  class WrapperComponent extends React.Component {
    state = {
      isLoading: true
    };

    componentDidMount() {
      const { $moduleConfig } = this.props;

      $moduleConfig.fetchModuleConfig('hybridmap', { force: true }).then((legacyCompanySettings) => {
        // when we first migrated off local storage, we moved settings to a module config
        // however the module config saves at the company level when we want to save at the user level

        // We previously persisted the top toolbar settings in localstorage
        // The saved searches are of particular interest because someone may have spent significant time building them
        // Check if any exist in local storage and migrate to the module config
        const legacySettings =
          safelyParseJSON(window.localStorage.getItem('hybrid-map-settings')) || legacyCompanySettings;

        // if we had a module config, once cleaned up it would just be an empty object
        if (legacySettings && Object.keys(legacySettings).length > 0) {
          this.saveSettings(legacySettings).then(() => {
            // load settings fresh
            this.loadSettings();

            if (legacySettings) {
              // clean up after the legacy settings
              window.localStorage.removeItem('hybrid-map-settings');
            }

            if (legacyCompanySettings && Object.keys(legacyCompanySettings).length > 0) {
              // clean up after the legacy company settings
              this.cleanLegacyCompanySettings();
            }
          });
        } else {
          this.loadSettings();
        }
      });
    }

    loadSettings = () => {
      const { $auth, $hybridMap } = this.props;
      const loadedSettings = deepClone($auth.userSettings.hybridMap || {});
      if (loadedSettings?.sidebarQueryOverrides) {
        loadedSettings.sidebarQueryOverrides.time_format = timezone.value;
      }

      this.setState({ isLoading: false }, () => {
        $hybridMap.settingsModel.set({
          ...loadedSettings,
          searchTerm: '' // always start with a fresh search box
        });
      });
    };

    cleanLegacyCompanySettings = () => {
      const { $moduleConfig } = this.props;
      $moduleConfig.saveModuleConfig({ module: 'hybridmap', settings: {} });
    };

    saveSettings = (settings, options = {}) => {
      const { $hybridMap, $auth } = this.props;
      const settingsToSave = settings || $hybridMap.sidebarSettings;

      // check for bypass of module save
      if (options && options.persist === false) {
        return Promise.resolve();
      }

      return $auth.setUserSettings({ hybridMap: settingsToSave });
    };

    render() {
      const { $hybridMap, $moduleConfig, $auth, ...restProps } = this.props;
      const { isLoading } = this.state;

      return (
        <Suspense
          loading={isLoading}
          fallback={
            <Flex flex={1} justifyContent="center">
              <Spinner />
            </Flex>
          }
        >
          <WrappedComponent
            settingsModel={$hybridMap.settingsModel}
            sidebarSettings={$hybridMap.sidebarSettings}
            saveSettings={this.saveSettings}
            {...restProps}
          />
        </Suspense>
      );
    }
  }

  return WrapperComponent;
}
